import { Component, DestroyRef, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs/operators';
import { FormControl, FormGroup, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TranslatePipe } from '@ngx-translate/core';
import { SolPreferencesService } from '../../../../../services/preferences/sol-preferences.service';
import { DropDownItem } from '../../../../../models/dtos';
import { CONSTANTS } from '../../../../../Constants/Constants';
import { IDisplayUnitFormGroup } from '../../../../../models/preferences/IDisplayUnitFormGroup';
import { StaticDropdownService } from '../../../../../services/common/static-dropdown.service';
import { NumericFieldConfig } from '../../../../../shared/numeric/numeric.types';
import { NumericMaskDirective } from '../../../../../shared/numeric/numeric-mask.directive';

@Component({
  selector: 'app-sol-preferences-display-units',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatInputModule,
    MatSelectModule,
    TranslatePipe,
    NumericMaskDirective
  ],
  templateUrl: './sol-preferences-display-units.component.html',
  styleUrls: ['./sol-preferences-display-units.component.scss']
})
export class SolPreferencesDisplayUnitsComponent implements OnInit {
  displayUnitsForm!: FormGroup<IDisplayUnitFormGroup>;
  projectUnits: DropDownItem[] = [];

  readonly numericFieldsConfig = {
    precisionConfig: {
      kind: 'integer',
      scale: CONSTANTS.ZERO,
      allowNegative: false,
      min: CONSTANTS.DECIMAL_PRECISION_MIN_VALUE,
      max: CONSTANTS.DECIMAL_PRECISION_MAX_VALUE,
      step: CONSTANTS.ONE,
      useGrouping: false,
      revertToLastValidValue: false
    },
    estimateDisplayConfig: {
      kind: 'integer',
      scale: CONSTANTS.ZERO,
      allowNegative: false,
      min: CONSTANTS.ESTIMATE_DISPLAY_MIN_VALUE,
      max: CONSTANTS.ESTIMATE_DISPLAY_MAX_VALUE,
      step: CONSTANTS.ONE,
      useGrouping: false,
      revertToLastValidValue: false
    },
    favouriteEstimateDisplayConfig: {
      kind: 'integer',
      scale: CONSTANTS.ZERO,
      min: CONSTANTS.FAVOURITE_ESTIMATE_DISPLAY_MIN_VALUE,
      max: CONSTANTS.FAVOURITE_ESTIMATE_DISPLAY_MAX_VALUE,
      step: CONSTANTS.ONE,
      useGrouping: false,
      revertToLastValidValue: false
    }
  } satisfies Record<string, NumericFieldConfig> 
  constructor(
    private readonly fb: NonNullableFormBuilder,
    private readonly preferencesService: SolPreferencesService,
    private readonly destroyRef: DestroyRef,
    private readonly staticDropdownService: StaticDropdownService
  ) {}

  ngOnInit(): void {
    this.initializeForm();
  }

  private initializeForm(): void {
    const preferences = this.preferencesService.getUserPreferences()?.preferences;
   

    if (!preferences) return;

    this.staticDropdownService.getProjectUnits()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((projectUnits) => {
        this.projectUnits = projectUnits;
      });

    this.displayUnitsForm = this.fb.group<IDisplayUnitFormGroup>({
      projectUnits: this.fb.control<number>(preferences.projectUnits ?? CONSTANTS.ONE),//this.resolveProjectUnit(preferences?.projectUnits, this.projectUnits)),
      precision: this.fb.control<number>(
        preferences.decimalPrecision ?? CONSTANTS.DECIMAL_PRECISION_MIN_VALUE, 
        [Validators.min(CONSTANTS.DECIMAL_PRECISION_MIN_VALUE), Validators.max(CONSTANTS.DECIMAL_PRECISION_MAX_VALUE)]),
      estimateDisplay: this.fb.control<number>(
        preferences?.mostRecentlyOpenedEstimateListMaxItemCount ?? CONSTANTS.ESTIMATE_DISPLAY_MAX_VALUE,
        [Validators.min(CONSTANTS.ESTIMATE_DISPLAY_MIN_VALUE), Validators.max(CONSTANTS.ESTIMATE_DISPLAY_MAX_VALUE)]
      ),
      favouriteEstimateDisplay: this.fb.control<number>(
        preferences?.favoriteEstimateMaxItemCount ?? CONSTANTS.FAVOURITE_ESTIMATE_DISPLAY_MAX_VALUE,
        [Validators.min(CONSTANTS.FAVOURITE_ESTIMATE_DISPLAY_MIN_VALUE), Validators.max(CONSTANTS.FAVOURITE_ESTIMATE_DISPLAY_MAX_VALUE)]
      )
    });

    // Sync every form change back to the shared service
    this.displayUnitsForm.valueChanges
      .pipe(
        filter(() => this.displayUnitsForm.valid),
        debounceTime(300),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(value => {
        this.preferencesService.silentSetPreferences(data => {
          data.preferences.projectUnits = value.projectUnits;
          data.preferences.decimalPrecision = value.precision;
          data.preferences.mostRecentlyOpenedEstimateListMaxItemCount = value.estimateDisplay;
          data.preferences.favoriteEstimateMaxItemCount = value.favouriteEstimateDisplay;
        });
      });
  }
  clampToMin(controlName: keyof IDisplayUnitFormGroup, min: number): void {
  const control = this.displayUnitsForm.controls[controlName];
  const value = control.value as number | null;
  if (value === null || value === undefined) {
    control.setValue(min);
  }
}
}
